import cx from 'classnames';
import { FC, SVGAttributes, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ampli } from '../../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../../core/Tracking/Amplitude/Amplitude';
import { Anchor } from '../../anchor/Anchor';
import { Typography } from '../../typography/Typography';

import style from './TransportCard.module.css';

type TransportCardProps = {
    recommended?: boolean;
    isCompact?: boolean;
    title: string;
    subtitle: string;
    description: string;
    linkUrl: string;
    linkLabel: string;
    Logo: FC<SVGAttributes<SVGElement>>;
    trackingInformation: {
        transportSearchClicked: TrackingProperties<typeof ampli.transportSearchClicked>;
    };
    className?: string;
};

export function TransportCard({
    title,
    subtitle,
    description,
    recommended,
    isCompact = false,
    linkUrl,
    linkLabel,
    Logo,
    trackingInformation,
    className,
}: TransportCardProps) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const handleClickLink = useCallback(() => {
        trackEvent((ampli, defaultProperties) => {
            ampli.transportSearchClicked({
                ...defaultProperties,
                ...trackingInformation.transportSearchClicked,
            });
        });
    }, [trackEvent, trackingInformation.transportSearchClicked]);

    return (
        <div className={cx(style.card, { [style.compact]: isCompact }, className)}>
            <div className={style.header}>
                <Logo className={style.logo} />
                {recommended && !isCompact ? (
                    <Typography scale="primary-xxs-bold" className={style.badge}>
                        {formatMessage({ id: 'global.component.transports.train.tag' })}
                    </Typography>
                ) : null}
            </div>

            <Typography
                as="div"
                scale={isCompact ? 'primary-body-regular' : 'primary-s-extra-bold'}
                className={style.title}
            >
                {title}
            </Typography>
            {!isCompact ? (
                <>
                    <Typography as="p" scale="primary-body-bold" className={style.subtitle}>
                        {subtitle}
                    </Typography>
                    <Typography as="p" scale="primary-body-regular" className={style.description}>
                        {description}
                    </Typography>
                </>
            ) : null}
            <Anchor obfuscate href={linkUrl} onClick={handleClickLink} target="_blank">
                {linkLabel}
            </Anchor>
        </div>
    );
}
